import React from "react";

import { DataImportPipelineJobHistoryService } from "~/features/dataImportProjects/services/dataImportPipelineJobHistory.service";
import type { DryRunHistoryItem, DryRunHistoryItemDataImportPipelineJob } from "~/features/dataImportProjects/types";

type UseListHistoryReturn<T = DryRunHistoryItem> = {
  history: T[] | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
};

function useListDryRunHistory<T = DryRunHistoryItem>(jobType: string, projectId?: string): UseListHistoryReturn<T> {
  const [history, setHistory] = React.useState<T[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  const historyService = React.useMemo(() => new DataImportPipelineJobHistoryService(), []);

  const fetchHistory = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await historyService.listHistoryItemsByJobType(jobType);

      // Filter by projectId if provided
      const filteredItems = projectId
        ? items.filter((item) => "dataImportProjectId" in item && item.dataImportProjectId === projectId)
        : items;

      setHistory(filteredItems as T[]);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch history"));
    } finally {
      setLoading(false);
    }
  }, [historyService, jobType, projectId]);

  React.useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  React.useEffect(() => {
    const handleStorageUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{ jobType: string }>;
      if (customEvent.detail.jobType === jobType) {
        fetchHistory();
      }
    };

    window.addEventListener("dryRunHistoryUpdated", handleStorageUpdate);
    return () => {
      window.removeEventListener("dryRunHistoryUpdated", handleStorageUpdate);
    };
  }, [jobType, fetchHistory]);

  return { history, loading, error, refetch: fetchHistory };
}

export function useListDataImportPipelineJobDryRunHistory(projectId?: string) {
  return useListDryRunHistory<DryRunHistoryItemDataImportPipelineJob>("DataImportPipelineJob", projectId);
}
